/**
 * SB6-II-01 — projects/v1 inspection-instructions wire types.
 *
 * Hand-rolled from the projects/v1@2.2.0 contract sketch in
 * `_STORIES/cross-cutting/SB6-II-01-inspection-instruction-page.md`
 * (the inspection-instructions resource is an additive extension of
 * the projects/v1 domain — OQ-II-0). Mirrors `../projects/types.ts`
 * style: closed `as const` arrays for the enums so the FE can iterate /
 * switch without a stringly-typed fallthrough.
 *
 * The BE serves these under `/api/projects/v1/inspection-instructions`.
 */

/** The canonical 9-value InspectionInstructionState enum
 *  (`libs/dto/src/inspections/inspection-instruction-state.enum.ts`). */
export const INSPECTION_INSTRUCTION_STATES = [
  "CREATED",
  "SCHEDULED",
  "ONSITE_EXECUTION",
  "DATA_TRANSFER",
  "DATA_PROCESSING",
  "ANALYSIS",
  "COMPLETED",
  "WITHDRAWN",
  "REJECTED",
] as const;
export type InspectionInstructionState =
  (typeof INSPECTION_INSTRUCTION_STATES)[number];

/** States that show a **Withdraw** transition button in the Edit modal. */
export const II_WITHDRAWABLE_STATES = ["CREATED", "SCHEDULED"] as const;
/** States that show a **Reject** transition button in the Edit modal. */
export const II_REJECTABLE_STATES = [
  "ONSITE_EXECUTION",
  "DATA_TRANSFER",
  "DATA_PROCESSING",
  "ANALYSIS",
] as const;
/** Terminal states — show **Reactivate** (not-implemented popup). */
export const II_TERMINAL_STATES = [
  "COMPLETED",
  "WITHDRAWN",
  "REJECTED",
] as const;

/** SYNC: projects/v1@2.6.0 (TNCY-8) — which side of the cross-tenant fence an
 *  II row is on. `owner` = an II of the caller's own project (default
 *  `scope=own`); `member` = an II visible cross-tenant because it is
 *  assigned-out to the caller (`assigned_to_tenant_id`, SB6-II-03 / XT-D1) OR
 *  a grant exposes IIs (`project_contractor` / `reporting_subcontractor`,
 *  XT-D22). On `scope=member` the BE composes + de-duplicates the assigned-out
 *  ∪ grant-exposed sets server-side (a row that is both appears once). */
export type IiOwnership = "owner" | "member";

/** SYNC: projects/v1@2.6.0 (TNCY-8) — read/list scope. `own` (default) =
 *  caller's own IIs; `member` = the cross-tenant assigned-out ∪ grant set,
 *  deduped server-side. The FE fires both and merges (`useOwnerMemberIIs`). */
export type IiScope = "own" | "member";

/** SYNC: projects/v1@2.6.0 / XT-D22 — the caller's effective access role on a
 *  `member` II. `ii_subcontractor` = an assigned-out-only row (no exposing
 *  grant); null on `owner` rows. Mirrors the `tenancy/v1` AccessRole enum. */
export const II_ACCESS_ROLES = [
  "project_customer",
  "project_contractor",
  "contract_customer",
  "contract_contractor",
  "reporting_subcontractor",
  "ii_subcontractor",
] as const;
export type IiAccessRole = (typeof II_ACCESS_ROLES)[number];

export const II_SORT_KEYS = [
  "instructionCode",
  "projectName",
  "siteName",
  "assetName",
  "pilotName",
  "assignedCompany",
  "plannedDate",
  "state",
] as const;
export type IiSortKey = (typeof II_SORT_KEYS)[number];

export type IiSortDir = "asc" | "desc";

/** SYNC: projects/v1@2.22.0 — provenance of an inspection instruction.
 *  `PROJECT` (default) and `SUBCONTRACTOR` are planned server-side and fully
 *  linked to a project + contract position. `FIELD_CREATED` = pilot-created in
 *  the field (AI3 `createStandaloneDataAcquisition`), an ORPHAN row unlinked
 *  from project/contract until back-office links it — `projectId`/`projectName`/
 *  `contractId`/`contractPositionId` are null on such rows. Required (the DDL
 *  column is NOT NULL DEFAULT 'PROJECT'). */
export const II_SOURCES = [
  "PROJECT",
  "SUBCONTRACTOR",
  "FIELD_CREATED",
] as const;
export type IiSource = (typeof II_SOURCES)[number];

/** A single assignee chip — `{first} {last} ({tenant})`.
 *  Name/tenant are nullable on the wire (contract IiAssignedUser); render via
 *  `userLabel()` which degrades gracefully (SB6-II-05).
 *
 *  TNCY-6 §e / XT-D9 — cross-tenant assignees from a GRANTED tenant whose
 *  grant withholds names come back with null first/last. `namesHidden` +
 *  `placeholder` are OPTIONAL forward-compat fields: when the BE marks an
 *  assignee names-withheld it sends a tenant-scoped `placeholder` (e.g.
 *  "CopterArt user"); the FE also SYNTHESISES one when names are null so the
 *  chip never reads "(Tenant)" alone. The user stays assignable by `userId`
 *  regardless. */
export interface AssignedUser {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  tenantName: string | null;
  /** True when the grant withholds this user's name (XT-D9). Optional —
   *  inferred from null first/last when the BE doesn't send it. */
  namesHidden?: boolean;
  /** Tenant-scoped stand-in shown when names are withheld (XT-D9). */
  placeholder?: string | null;
}

export interface IiListItem {
  inspectionInstructionId: string;
  instructionCode: number;
  /** SYNC: projects/v1@2.22.0 — provenance; `FIELD_CREATED` rows are orphans
   *  with null project/contract until back-office links them. */
  source: IiSource;
  /** SYNC: projects/v1@2.22.0 — nullable: null on a FIELD_CREATED (orphan) II. */
  projectId: string | null;
  /** SYNC: projects/v1@2.22.0 — nullable: null on a FIELD_CREATED (orphan) II. */
  projectName: string | null;
  /** SYNC: projects/v1@2.24.0 (DI-2-12) — the project's `workflow` as stored;
   *  null without a project, for a project without one, or a deleted one. The
   *  intake shows its workflow picker exactly when this is null (DI-D9). */
  projectWorkflow: string | null;
  siteId: string | null;
  siteName: string | null;
  assetId: string | null;
  /** BE-composed asset name (manufacturer abbreviation + identifier + park no). */
  assetName: string | null;
  pilotName: string | null;
  assignedUsers: AssignedUser[];
  assignedCompanyTenantId: string | null;
  assignedCompanyName: string | null;
  /** True when the parent project is owned by the current tenant — the
   *  Assigned Company cell is interactive only then (OQ-II-8). */
  assignedCompanyEditable: boolean;
  /** YYYY-MM-DD (date-only — OQ-II-2). */
  plannedDate: string | null;
  /** SYNC: projects/v1@2.21.0 — nullable (unlinked/legacy/imported rows). */
  contractId: string | null;
  /** SYNC: projects/v1@2.21.0 — nullable (unlinked/legacy/imported rows). */
  contractPositionId: string | null;
  aiFindingRequired: boolean | null;
  aiComponentSegmentationRequired: boolean | null;
  state: InspectionInstructionState;
  /** SYNC: projects/v1@2.6.0 (TNCY-8): `owner` | `member`. Optional for
   *  back-compat with a pre-2.6.0 BE; always present from 2.6.0. Absent ⇒
   *  treated as `owner`. This is a DISTINCT signal from `assignedCompanyName`
   *  (who the II is assigned OUT to) — see `columns.tsx` AC-2 note. */
  ownership?: IiOwnership;
  /** SYNC: projects/v1@2.6.0 (TNCY-8): the data-owning tenant's display name
   *  on a `member` row; null on `owner` rows. */
  ownerTenantName?: string | null;
  /** SYNC: projects/v1@2.6.0 (TNCY-8): the caller's effective access role on a
   *  `member` II's project (XT-D22); null on `owner` rows. */
  role?: IiAccessRole | null;
}

export interface IiListResponse {
  items: IiListItem[];
  total: number;
  page: number;
  pageSize: number;
}

/** `{provider, id}` external-id row — reused by the Site-modal pattern. */
export interface IiExternalId {
  provider: string;
  id: string;
}

/** Eager / async facet option (`{id, label}`). */
export interface IiFacetOption {
  id: string;
  label: string;
}
export interface IiFacetResponse {
  items: IiFacetOption[];
}

export interface IiAssignableUsersResponse {
  users: AssignedUser[];
}

/** Single-select cascade options (`options/projects`, `options/contracts`,
 *  `options/contract-positions`). */
export interface IiCascadeOption {
  id: string;
  label: string;
}
export interface IiCascadeOptionsResponse {
  items: IiCascadeOption[];
}

// Matches the contract `IiOptionsResponse` item shape ({id,label}) — same as
// IiCascadeOption. The `id` is a tenant UUID, `label` the tenant display name
// (TNCY-2 repointed this picker to the cross-tenant known-tenants set).
export interface IiCompanyOption {
  id: string;
  label: string;
}
export interface IiCompanyOptionsResponse {
  items: IiCompanyOption[];
}

/** Detail payload behind `GET /inspection-instructions/{id}`. */
export interface IiDetail {
  inspectionInstructionId: string;
  instructionCode: number;
  /** SYNC: projects/v1@2.22.0 — provenance; `FIELD_CREATED` rows are orphans
   *  with null project/contract until back-office links them. */
  source: IiSource;
  /** SYNC: projects/v1@2.22.0 — nullable: null on a FIELD_CREATED (orphan) II. */
  projectId: string | null;
  /** SYNC: projects/v1@2.22.0 — nullable: null on a FIELD_CREATED (orphan) II. */
  projectName: string | null;
  /** SYNC: projects/v1@2.24.0 (DI-2-12) — the project's `workflow` as stored;
   *  null without a project, for a project without one, or a deleted one. The
   *  intake shows its workflow picker exactly when this is null (DI-D9). */
  projectWorkflow: string | null;
  /** SYNC: projects/v1@2.21.0 — nullable (unlinked/legacy/imported rows). */
  contractId: string | null;
  contractName: string | null;
  /** SYNC: projects/v1@2.21.0 — nullable (unlinked/legacy/imported rows). */
  contractPositionId: string | null;
  contractPositionName: string | null;
  siteId: string | null;
  siteName: string | null;
  assetId: string | null;
  assetName: string | null;
  aiFindingRequired: boolean | null;
  aiComponentSegmentationRequired: boolean | null;
  notes: string | null;
  externalIds: IiExternalId[];
  state: InspectionInstructionState;
  // BE returns a user-ref OBJECT (firstName/lastName/tenantName), not a string —
  // composed for display via composeUserRef() in EditInspectionInstructionDialog.
  // (Previously typed `string | null`, which rendered as "[object Object]".)
  createdBy: IiUserRef | null;
  createdAt: string | null;
  updatedBy: IiUserRef | null;
  updatedAt: string | null;
  deletedBy: IiUserRef | null;
  deletedAt: string | null;
}

/** Audit user reference (created_by / updated_by / deleted_by). The BE sends an
 *  empty-but-present object ({firstName:null,...}) when the UUID can't be
 *  resolved to a named auth_db user. */
export interface IiUserRef {
  firstName: string | null;
  lastName: string | null;
  tenantName: string | null;
}

export interface ListInspectionInstructionsParams {
  q?: string;
  projectIds?: readonly string[];
  siteIds?: readonly string[];
  assetIds?: readonly string[];
  states?: readonly InspectionInstructionState[];
  showCompleted?: boolean;
  sortBy?: IiSortKey;
  sortDir?: IiSortDir;
  page?: number;
  pageSize?: number;
  /** SYNC: projects/v1@2.6.0 (TNCY-8): `own` (default) | `member` (the
   *  cross-tenant assigned-out ∪ grant set, deduped server-side). Omitted ⇒
   *  BE defaults to `own`. The FE fires both and merges (`useOwnerMemberIIs`). */
  scope?: IiScope;
  /** SYNC: projects/v1@2.23.0 (FCII) — orphan filter. `unlinked` returns only
   *  project-less / field-created IIs (`project_id IS NULL`); `linked` the
   *  complement; omitted ⇒ BE default `all`. Derived from project_id, not
   *  `source`. */
  linkage?: "linked" | "unlinked";
}

/** POST /inspection-instructions body. */
export interface CreateInspectionInstructionBody {
  contractPositionId: string;
  aiFindingRequired?: boolean;
  aiComponentSegmentationRequired?: boolean;
  notes?: string;
  externalIds?: IiExternalId[];
}

/** PATCH /inspection-instructions/{id} body — any subset. */
export interface PatchInspectionInstructionBody {
  assignedUserIds?: string[];
  assignedCompanyTenantId?: string | null;
  plannedDate?: string | null;
  notes?: string;
  externalIds?: IiExternalId[];
  aiFindingRequired?: boolean;
  aiComponentSegmentationRequired?: boolean;
  contractPositionId?: string;
}

/** POST /inspection-instructions/{id}/transition body. */
export interface TransitionInspectionInstructionBody {
  action: "WITHDRAW" | "REJECT";
}

/** POST /inspection-instructions/bulk-assign body. */
export interface BulkAssignBody {
  inspectionInstructionIds: string[];
  userIds: string[];
}

export const II_NOTES_MAX = 10000;
